import { Box, Button, Stack } from "@tm/components"
import { memo, useRef, useState } from "react"
import { ArticleErpInfo, WheelsBundleParams } from "@tm/models"
import Morpheus from "@tm/morpheus"
import VendorScrollbar from "react-custom-scrollbars-2"
import { AvailabilityFilterType } from "@bundles/parts/business"
import { Scrollbar } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { ArticleListConfigurationProvider } from "../ListV2/ArticleListConfiguration"
import { ArticleListActionsProvider } from "../ListV2/ContextProvider/ArticleListActions"
import { TyresError } from "../../../../tyres/src/components/list/components"
import { NextPageLoader } from "../ListV2/components/NextPageLoader"
import { ComposerComponent, ContextComposer } from "../ListV2/ContextProvider"
import { BasketQuantitiesProvider } from "../ListV2/ContextProvider/BasketQuantities"
import { ListOptionsProvider } from "../ListV2/ContextProvider/ListOptions"
import { ErpInfosProvider } from "../ListV2/ContextProvider/ErpInfos"
import { NotesProvider } from "../ListV2/ContextProvider/Notes"
import { useWheelsListContext, WheelsListProvider } from "./ContextProvider/WheelsList"
import { EmptyListParamsProvider } from "../ListV2/ContextProvider/ListParams"
import { EmptyFiltersProvider } from "../ListV2/ContextProvider/Filters"
import { TradeReferenceProvider } from "../ListV2/ContextProvider/TradeReferences"
import { PreviouslyOrderedArticlesProvider } from "../ListV2/ContextProvider/PreviouslyOrderedArticles"
import { ActiveVehicleDataProvidersProvider } from "../ListV2/ContextProvider/ActiveVehicleDataProviders"
import { ProductGroupRepairTimesProvider } from "../ListV2/ContextProvider/ProductGroupRepairTimes"
import { WheelsArticleItem } from "./WheelsArticleItem"
import { useParticularContext, WheelsListType } from "../PureList/ContextProviders/Particular/ParticularContext"
import { ArticleGroupHeader } from "../list/components/ArticleGroupHeader/ArticleGroupHeader"
import { SupplierLogosProvider } from "../ListV2/ContextProvider/SupplierLogos"
import { useResizeErpColumnInDefaultArticleItem } from "../ListV2/hooks/useResizeErpContainerColumn"

export function WheelsList() {
    const [hasListRendered, setHasListRendered] = useState(false)
    const [erpInfos, setErpInfos] = useState<ArticleErpInfo[]>([]) // provider can hold a global state, where useErpInfos would create a context dependent state

    const components: ComposerComponent[] = [
        [ArticleListConfigurationProvider],
        [EmptyListParamsProvider],
        [EmptyFiltersProvider],
        [WheelsListProvider, { setHasRendered: setHasListRendered, erpInfos }],
        [SupplierLogosProvider, { isEnabled: hasListRendered }],
        [TradeReferenceProvider, { isEnabled: hasListRendered }],
        [ErpInfosProvider, { isEnabled: hasListRendered, erpInfos, setErpInfos }],
        [BasketQuantitiesProvider, { isEnabled: hasListRendered }],
        [ActiveVehicleDataProvidersProvider],
        [ProductGroupRepairTimesProvider, { isEnabled: hasListRendered }],
        [PreviouslyOrderedArticlesProvider, { isEnabled: hasListRendered }],
        [NotesProvider, { isEnabled: hasListRendered, isVehicleDepending: false }],
        [ListOptionsProvider, { isEnabled: true }],
        [ArticleListActionsProvider],
    ]

    return (
        <ContextComposer components={components}>
            <WheelsListComponent />
        </ContextComposer>
    )
}

const WheelsListComponent = memo(() => {
    const scrollbarRef = useRef<VendorScrollbar | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const fetchingLockRef = useRef(false)
    const { translateText } = useLocalization()

    const wheelsArticlesUnfiltered = useWheelsListContext((wheelsContext) => wheelsContext.articles)
    const wheelsArticles = useWheelsListContext((wheelsContext) => wheelsContext.displayArticles)
    const isLoading = useWheelsListContext((wheelsContext) => wheelsContext.isLoading)
    const isSuccess = useWheelsListContext((wheelsContext) => wheelsContext.isSuccess)
    const isFailed = useWheelsListContext((wheelsContext) => wheelsContext.isFailed)
    const isStalling = useWheelsListContext((wheelsContext) => wheelsContext.isStalling)
    const sorting = useWheelsListContext((wheelsContext) => wheelsContext.sorting)
    const fetchNextPage = useWheelsListContext((wheelsContext) => wheelsContext.loadNextPage)
    const isFetchingNextPage = useWheelsListContext((wheelsContext) => wheelsContext.isFetchingNextPage)
    const hasNextPage = useWheelsListContext((wheelsContext) => wheelsContext.hasNextPage)
    const isInitialSorting = useWheelsListContext((wheelsContext) => wheelsContext.isInitialSorting)
    const isSorting = useWheelsListContext((wheelsContext) => wheelsContext.isSorting)
    const isFetchingAuto = useWheelsListContext((wheelsContext) => wheelsContext.isFetchingAuto)

    const contextProvider = useParticularContext<WheelsListType>()
    const { request, headerTitle, headerTitleContent, selectedAvailability } = contextProvider ?? {}

    const displayNextPageButton =
        !!selectedAvailability && wheelsArticles && wheelsArticles.length < wheelsArticlesUnfiltered.length && !isFetchingNextPage && !isFetchingAuto

    const { maximumSortableArticleCount } = Morpheus.getParams<WheelsBundleParams>("tyres")

    useResizeErpColumnInDefaultArticleItem(containerRef.current, !!wheelsArticles?.length)

    const isInitialLoading = isLoading && !isFetchingNextPage
    const scrollDisabled = isInitialLoading || isInitialSorting || isSorting || isStalling

    const fetchPageSafely = async () => {
        fetchingLockRef.current = true
        try {
            await fetchNextPage()
        } finally {
            fetchingLockRef.current = false
        }
    }

    const handleScroll = () => {
        if (!scrollbarRef.current) {
            return
        }
        if (scrollDisabled || selectedAvailability !== AvailabilityFilterType.None) {
            return
        }

        const scrollBar = scrollbarRef.current
        const nearBottom = scrollBar.getScrollHeight() - scrollBar.getScrollTop() <= scrollBar.getClientHeight() + 25

        if (nearBottom && !fetchingLockRef.current) {
            fetchPageSafely()
        }
    }

    const displayError = ((isSuccess && !wheelsArticles?.length) || isFailed) && !isLoading && !isFetchingNextPage && !isFetchingAuto

    if (!request) {
        return null
    }
    if ((isInitialLoading || isStalling || isInitialSorting) && !isFetchingAuto) {
        return <NextPageLoader skeletonCategory skeletonArticleCount={5} />
    }

    return (
        <Stack flex={1} position="relative">
            {!displayError && (
                <ArticleGroupHeader
                    title={headerTitle || ""}
                    titleContent={headerTitleContent}
                    groupArticlesCount={wheelsArticles?.length}
                    currentSorting={sorting?.selectedSorting}
                    onChangeSorting={maximumSortableArticleCount ? sorting?.handleSorting : undefined}
                    maximumSortableArticleCount={maximumSortableArticleCount}
                />
            )}
            <Scrollbar
                isScrollDisabled={scrollDisabled}
                onScroll={handleScroll}
                onRef={(el) => {
                    scrollbarRef.current = el
                }}
            >
                <Box padding={0.5} ref={containerRef}>
                    {displayError && <TyresError />}
                    {wheelsArticles?.map((article) => <WheelsArticleItem key={article.internalId} article={article} />)}
                    {isFetchingNextPage && <NextPageLoader canLoadNextArticles skeletonArticleCount={1} />}
                </Box>
                {!!displayNextPageButton && hasNextPage && (
                    <Box display="flex" justifyContent="center">
                        <Button
                            color="highlight"
                            onClick={() => {
                                fetchPageSafely()
                            }}
                        >
                            {translateText(12430)}
                        </Button>
                    </Box>
                )}
            </Scrollbar>
        </Stack>
    )
})
